import { Suspense } from "react";
import { Navigate, useParams, useRoutes, useSearchParams } from "react-router";
import { BaseShell, HeaderLessShell, MiniShell } from "../shell/Shell.jsx";
import { Spinner } from "../../design-system/utils/Spinner/Spinner.jsx";
import LibraryOverview from "../library/LibraryOverview.jsx";
import FullLibraryItem from "../library/FullLibraryItem.jsx";
import NotFoundPage from "../not-found/NotFoundPage.jsx";
import RouteBoundary from "../../lib/RouteBoundary";
import { ROUTES } from "../../routes.js";
import { MODELS, useModel } from "../../api";
import SparkStepOverview from "./spark-step/SparkStepOverview";
import SparkShowroomOverview from "./showroom/SparkShowroomOverview";
import GlossaryOverview from "./glossary/GlossaryOverview";
import HandbookOverview from "./handbook/HandbookOverview";
import { flags } from "../../flags";
import { ClassroomManagementOverlay } from "./classroom-management/ClassroomManagementOverlay";
import { FeedbackFormHost } from "../feedback/FeedbackFormHost";
import OptionsOverview from "./workshop-settings/OptionsOverview";
import { WorkshopUserTour } from "./user-tour/WorkshopUserTour.jsx";
import SparkOverviewScreen from "./spark-overview/SparkOverview";
import InviteOverview from "./invite-overview/InviteOverview";
import FullShowroomEntry from "./showroom/FullShowroomEntry";
import ShowroomForm from "./showroom-form/ShowroomForm";
import type { SparkBag } from "./sparkBag";
import type { Workshop } from "../../api/workshop";
import type { Spark } from "../../api/spark";

// Todo: Add "getUrl" functions, use these in the toolbar etc.
// Everything below the spark's index that needs nothing from the shell. The
// routes whose screens take spark data as a prop are built inside the
// component instead, where that data exists.
const routes = [
  { path: "/library", element: <LibraryOverview /> },
  { path: "/library/:libraryItemId", element: <FullLibraryItem /> },
  // Behind the flag the toolbar links out to digital-sparks.org instead.
  ...(flags.handbook
    ? [{ path: "/handbook", element: <HandbookOverview /> }]
    : []),
  // Unknown sub-route is an explicit, in-shell 404 — no silent redirect.
  { path: "*", element: <NotFoundPage /> },
];

function SparkShell() {
  const { workshopSlug, sparkSlug: urlSparkSlug } = useParams<{
    workshopSlug: Workshop["slug"];
    sparkSlug: Spark["slug"];
  }>();
  // Suspends while loading; a bad workshop slug 404s to the outer RouteBoundary.
  // Always present in practice — this shell only ever mounts inside a
  // matched `/w/:workshopSlug/...` route — but react-router types every
  // param as possibly absent regardless.
  const workshop = useModel(MODELS.WORKSHOP({ slug: workshopSlug ?? "" }));
  const spark = workshop.spark;
  const structure = spark?.structure;
  const settings = workshop.settings;

  const [searchParams] = useSearchParams();
  const shouldShowTour = searchParams.has("tour");

  // The spark data this shell hands to everything it renders as a prop.
  // Assembled before the guards below because `useRoutes` is a hook — with no
  // spark the element it builds is thrown away unrendered.
  const sparkBag: SparkBag = { workshop, spark, structure, settings };

  const element = useRoutes([
    {
      path: "/",
      element: <SparkOverviewScreen bag={sparkBag} />,
    },
    {
      path: "/step/:stepSlug",
      element: <SparkStepOverview bag={sparkBag} />,
    },
    {
      path: "/team-diary/:smallTeamId",
      element: <ShowroomForm spark={spark} />,
    },
    {
      path: "/options",
      element: <OptionsOverview bag={sparkBag} />,
    },
    {
      path: "/showroom",
      element: <SparkShowroomOverview bag={sparkBag} />,
    },
    {
      path: "/showroom/:smallTeamId",
      element: <FullShowroomEntry bag={sparkBag} />,
    },
    { path: "/invite", element: <InviteOverview workshop={workshop} /> },
    { path: "/glossary", element: <GlossaryOverview bag={sparkBag} /> },
    ...routes,
  ]);

  // A workshop without a spark is broken data, not a route the user can fix.
  if (!spark) {
    return (
      <HeaderLessShell>
        <NotFoundPage />
      </HeaderLessShell>
    );
  }

  // Redirect if we have an outdated url from back when a workshop could have
  // multiple sparks — a real redirect to the canonical spark, not a 404.
  if (urlSparkSlug !== spark.slug) {
    return (
      <Navigate
        to={ROUTES.spark.getUrl({ workshopSlug, sparkSlug: spark.slug })}
        replace
      />
    );
  }

  // In all other cases, render the correct component for the specified route.
  return (
    <>
      <ClassroomManagementOverlay workshop={workshop} />
      <FeedbackFormHost workshop={workshop} />

      <BaseShell>
        {shouldShowTour && <WorkshopUserTour bag={sparkBag} />}
        <RouteBoundary Shell={MiniShell}>
          <Suspense fallback={<Spinner />}>{element}</Suspense>
        </RouteBoundary>
      </BaseShell>
    </>
  );
}

export default SparkShell;
